// src/components/FbPostModal.js
// Facebook post modal — Tara generates caption, seller controls everything

import { useState } from 'react'
import { supabase } from '../supabase'

const NAVY  = '#0B1F3A'
const TEAL  = '#2DD4B0'

export default function FbPostModal({ product, seller, onClose, onSuccess }) {
  const [step, setStep] = useState('settings') // settings → preview → done

  // ── Settings ──
  const [postLanguage,  setPostLanguage]  = useState(seller?.preferred_language === 'bn' ? 'bengali' : 'english')
  const [postStyle,     setPostStyle]     = useState('casual')
  const [ctaType,       setCtaType]       = useState('inbox')
  const [priceDisplay,  setPriceDisplay]  = useState('tara') // 'show' | 'hide' | 'tara'
  const [scheduleMode,  setScheduleMode]  = useState(false)
  const [scheduleTime,  setScheduleTime]  = useState('')

  // ── Preview ──
  const [caption,      setCaption]      = useState('')
  const [priceRec,     setPriceRec]     = useState(null)   // Tara's price recommendation
  const [generating,   setGenerating]   = useState(false)
  const [posting,      setPosting]      = useState(false)
  const [postUrl,      setPostUrl]      = useState(null)
  const [error,        setError]        = useState('')

  const sym = seller?.seller_currency === 'BDT' ? '৳'
            : seller?.seller_currency === 'USD' ? '$'
            : seller?.seller_currency === 'AUD' ? 'A$' : '৳'

  // ── Generate caption (preview_only = true) ──────────────────────────────
  const handleGenerate = async () => {
    setGenerating(true); setError('')
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('fb-post-product', {
        body: {
          product_name:       product.name,
          product_code:       product.product_code,
          category:           product.category,
          sell_price:         product.sell_price,
          additional_details: product.additional_details,
          color:              product.color,
          season:             product.season,
          image_url:          product.image_url,
          stock:              product.stock,
          shop_name:          seller?.shop_name || 'Our Shop',
          seller_currency:    seller?.seller_currency || 'BDT',
          post_language:      postLanguage,
          post_style:         postStyle,
          cta_type:           ctaType,
          whatsapp_number:    seller?.phone,
          price_display:      priceDisplay,
          fb_page_id:         seller?.fb_page_id   || null,
          fb_page_token:      seller?.fb_page_token || null,
          preview_only:       true,
        }
      })
      if (fnErr || !data?.success) throw new Error(fnErr?.message || data?.error || 'Generation failed')
      setCaption(data.caption)
      if (data.price_recommendation) setPriceRec(data.price_recommendation)
      setStep('preview')
    } catch (err) {
      setError(err.message || 'Failed to generate caption')
    }
    setGenerating(false)
  }

  // ── Post to Facebook ─────────────────────────────────────────────────────
  const handlePost = async () => {
    setPosting(true); setError('')
    try {
      const { data, error: fnErr } = await supabase.functions.invoke('fb-post-product', {
        body: {
          product_name:       product.name,
          product_code:       product.product_code,
          category:           product.category,
          sell_price:         product.sell_price,
          additional_details: product.additional_details,
          color:              product.color,
          season:             product.season,
          image_url:          product.image_url,
          stock:              product.stock,
          shop_name:          seller?.shop_name || 'Our Shop',
          seller_currency:    seller?.seller_currency || 'BDT',
          post_language:      postLanguage,
          post_style:         postStyle,
          cta_type:           ctaType,
          whatsapp_number:    seller?.phone,
          price_display:      priceDisplay,
          fb_page_id:         seller?.fb_page_id   || null,
          fb_page_token:      seller?.fb_page_token || null,
          caption_override:   caption,
          schedule_time:      scheduleMode && scheduleTime ? scheduleTime : null,
          preview_only:       false,
        }
      })
      if (fnErr || !data?.success) throw new Error(fnErr?.message || data?.error || 'Post failed')
      setPostUrl(data.post_url)
      setStep('done')
      onSuccess?.()
    } catch (err) {
      setError(err.message || 'Failed to post to Facebook')
    }
    setPosting(false)
  }

  // ── Reusable pill button ─────────────────────────────────────────────────
  const Pill = ({ value, current, onChange, children }) => (
    <button onClick={() => onChange(value)}
      style={{ padding:'7px 14px', borderRadius:'20px', border:'1px solid', borderColor: current===value ? NAVY : 'var(--border)', background: current===value ? NAVY : 'var(--bg-card)', color: current===value ? '#fff' : 'var(--text-muted)', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:'inherit', transition:'all 0.12s', whiteSpace:'nowrap' }}>
      {children}
    </button>
  )

  // ── Toggle ───────────────────────────────────────────────────────────────
  const Toggle = ({ value, onChange }) => (
    <button onClick={() => onChange(!value)}
      style={{ width:'40px', height:'22px', borderRadius:'11px', background: value ? TEAL : 'var(--border)', border:'none', cursor:'pointer', position:'relative', transition:'background 0.2s', flexShrink:0 }}>
      <div style={{ position:'absolute', top:'3px', left: value ? '21px' : '3px', width:'16px', height:'16px', borderRadius:'50%', background:'#fff', transition:'left 0.2s', boxShadow:'0 1px 3px rgba(0,0,0,0.2)' }} />
    </button>
  )

  const FB_BLUE = '#1877F2'

  return (
    <div onClick={e => e.target===e.currentTarget && onClose()}
      style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.55)', zIndex:200, display:'flex', alignItems:'center', justifyContent:'center', padding:'16px', fontFamily:'Space Grotesk, sans-serif' }}>
      <div style={{ background:'var(--bg-card)', borderRadius:'20px', width:'100%', maxWidth:'540px', maxHeight:'90vh', display:'flex', flexDirection:'column', boxShadow:'0 8px 40px rgba(0,0,0,0.2)', overflow:'hidden' }}>

        {/* ── Header ── */}
        <div style={{ padding:'16px 20px', borderBottom:'1px solid var(--border)', display:'flex', alignItems:'center', justifyContent:'space-between', flexShrink:0 }}>
          <div style={{ display:'flex', alignItems:'center', gap:'10px' }}>
            <div style={{ width:'32px', height:'32px', borderRadius:'8px', background:FB_BLUE, display:'flex', alignItems:'center', justifyContent:'center' }}>
              <svg width="16" height="16" viewBox="0 0 24 24" fill="#fff"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
            </div>
            <div>
              <div style={{ fontSize:'14px', fontWeight:700, color:'var(--text-primary)' }}>Post to Facebook</div>
              <div style={{ fontSize:'11.5px', color:'var(--text-muted)' }}>{product.name} · {product.product_code}</div>
            </div>
          </div>
          <button onClick={onClose} style={{ background:'none', border:'none', cursor:'pointer', color:'var(--text-muted)', fontSize:'20px', lineHeight:1, padding:'4px' }}>×</button>
        </div>

        <div style={{ overflowY:'auto', flex:1 }}>

          {/* ══ STEP: Settings ══════════════════════════════════════════════ */}
          {step === 'settings' && (
            <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:'18px' }}>

              {/* Product preview */}
              <div style={{ display:'flex', gap:'12px', padding:'12px', background:'var(--bg-muted)', borderRadius:'12px' }}>
                {product.image_url
                  ? <img src={product.image_url} alt={product.name} style={{ width:'52px', height:'52px', borderRadius:'8px', objectFit:'cover', flexShrink:0 }} />
                  : <div style={{ width:'52px', height:'52px', borderRadius:'8px', background:'var(--border)', flexShrink:0, display:'flex', alignItems:'center', justifyContent:'center' }}>
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="var(--text-muted)" strokeWidth="1.5" strokeLinecap="round"><path d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z"/></svg>
                    </div>
                }
                <div style={{ flex:1, minWidth:0 }}>
                  <div style={{ fontSize:'13px', fontWeight:700, color:'var(--text-primary)' }}>{product.name}</div>
                  <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px' }}>{product.product_code} · {product.category}</div>
                  {product.sell_price && <div style={{ fontSize:'12px', color:'var(--success)', fontWeight:600, marginTop:'3px' }}>{sym}{Number(product.sell_price).toLocaleString()}</div>}
                </div>
              </div>

              {/* Language */}
              <div>
                <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>Caption language</div>
                <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                  <Pill value="english" current={postLanguage} onChange={setPostLanguage}>English</Pill>
                  <Pill value="bengali" current={postLanguage} onChange={setPostLanguage}>বাংলা</Pill>
                  <Pill value="mixed"   current={postLanguage} onChange={setPostLanguage}>Mixed</Pill>
                </div>
              </div>

              {/* Style */}
              <div>
                <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>Post style</div>
                <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                  <Pill value="casual"       current={postStyle} onChange={setPostStyle}>Casual</Pill>
                  <Pill value="professional" current={postStyle} onChange={setPostStyle}>Professional</Pill>
                  <Pill value="festive"      current={postStyle} onChange={setPostStyle}>Festive</Pill>
                </div>
              </div>

              {/* CTA */}
              <div>
                <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>How should customers order?</div>
                <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                  <Pill value="inbox"    current={ctaType} onChange={setCtaType}>Inbox us</Pill>
                  <Pill value="comment"  current={ctaType} onChange={setCtaType}>Comment BOOK</Pill>
                  <Pill value="whatsapp" current={ctaType} onChange={setCtaType}>WhatsApp</Pill>
                </div>
              </div>

              {/* Price display — 3 options */}
              <div>
                <div style={{ fontSize:'12px', fontWeight:600, color:'var(--text-muted)', marginBottom:'8px' }}>Price in post</div>
                <div style={{ display:'flex', gap:'7px', flexWrap:'wrap' }}>
                  <Pill value="hide" current={priceDisplay} onChange={setPriceDisplay}>Hide price</Pill>
                  <Pill value="show" current={priceDisplay} onChange={setPriceDisplay}>Show price</Pill>
                  <Pill value="tara" current={priceDisplay} onChange={setPriceDisplay}>Ask Tara</Pill>
                </div>
                <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'6px', lineHeight:1.5 }}>
                  {priceDisplay === 'hide' && 'Price will not appear in the post. Customers must inbox for price.'}
                  {priceDisplay === 'show' && `Price ${sym}${product.sell_price ? Number(product.sell_price).toLocaleString() : '—'} will appear in the post.`}
                  {priceDisplay === 'tara' && 'Tara will analyze your product and recommend whether to show or hide price.'}
                </div>
              </div>

              {/* Schedule */}
              <div>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', padding:'12px 14px', background:'var(--bg-muted)', borderRadius:'10px' }}>
                  <div>
                    <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)' }}>Schedule post</div>
                    <div style={{ fontSize:'11.5px', color:'var(--text-muted)', marginTop:'2px' }}>Post at a specific time instead of now</div>
                  </div>
                  <Toggle value={scheduleMode} onChange={setScheduleMode} />
                </div>
                {scheduleMode && (
                  <input type="datetime-local" value={scheduleTime} onChange={e => setScheduleTime(e.target.value)}
                    min={new Date(Date.now() + 10*60000).toISOString().slice(0,16)}
                    style={{ width:'100%', padding:'10px 14px', border:'1px solid var(--border)', borderRadius:'10px', fontSize:'13px', fontFamily:'inherit', color:'var(--text-primary)', background:'var(--bg-card)', outline:'none', boxSizing:'border-box', marginTop:'8px' }} />
                )}
              </div>

              {error && (
                <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'10px', fontSize:'12.5px', color:'#DC2626' }}>{error}</div>
              )}

              <button onClick={handleGenerate} disabled={generating}
                style={{ width:'100%', padding:'13px', borderRadius:'12px', background: generating ? 'var(--border)' : NAVY, border:'none', fontSize:'14px', fontWeight:700, color: generating ? 'var(--text-muted)' : '#fff', cursor: generating ? 'not-allowed' : 'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                {generating && <div style={{ width:'16px', height:'16px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'fb-spin 0.7s linear infinite' }} />}
                {generating ? 'Tara is writing your caption...' : 'Generate caption →'}
              </button>
            </div>
          )}

          {/* ══ STEP: Preview ═══════════════════════════════════════════════ */}
          {step === 'preview' && (
            <div style={{ padding:'20px', display:'flex', flexDirection:'column', gap:'14px' }}>

              {/* Tara price recommendation (only if "Ask Tara" was selected) */}
              {priceDisplay === 'tara' && priceRec && (
                <div style={{ background:'rgba(45,212,176,0.06)', border:'1px solid rgba(45,212,176,0.2)', borderRadius:'12px', padding:'14px 16px' }}>
                  <div style={{ display:'flex', alignItems:'center', gap:'8px', marginBottom:'8px' }}>
                    <div style={{ width:'20px', height:'20px', borderRadius:'50%', background:NAVY, display:'flex', alignItems:'center', justifyContent:'center', flexShrink:0 }}>
                      <svg width="10" height="10" viewBox="0 0 24 24" fill="none"><path d="M12 2C12 2 12.8 7.5 14.5 9.5C16.2 11.5 22 12 22 12C22 12 16.2 12.5 14.5 14.5C12.8 16.5 12 22 12 22C12 22 11.2 16.5 9.5 14.5C7.8 12.5 2 12 2 12C2 12 7.8 11.5 9.5 9.5C11.2 7.5 12 2 12 2Z" fill={TEAL}/></svg>
                    </div>
                    <div style={{ fontSize:'12.5px', fontWeight:700, color:NAVY }}>
                      Tara recommends: {priceRec.recommendation === 'hide' ? 'Hide price' : 'Show price'}
                    </div>
                  </div>
                  <div style={{ fontSize:'12px', color:'var(--text-muted)', lineHeight:1.6, marginBottom:'10px' }}>
                    {priceRec.reason}
                  </div>
                  <div style={{ display:'flex', gap:'7px' }}>
                    <button onClick={() => { setPriceDisplay('hide'); handleGenerate() }}
                      style={{ flex:1, padding:'8px', borderRadius:'8px', border:'1px solid var(--border)', background: priceRec.recommendation==='hide' ? NAVY : 'var(--bg-card)', color: priceRec.recommendation==='hide' ? '#fff' : 'var(--text-muted)', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                      Hide price
                    </button>
                    <button onClick={() => { setPriceDisplay('show'); handleGenerate() }}
                      style={{ flex:1, padding:'8px', borderRadius:'8px', border:'1px solid var(--border)', background: priceRec.recommendation==='show' ? NAVY : 'var(--bg-card)', color: priceRec.recommendation==='show' ? '#fff' : 'var(--text-muted)', fontSize:'12px', fontWeight:600, cursor:'pointer', fontFamily:'inherit' }}>
                      Show price
                    </button>
                  </div>
                </div>
              )}

              {/* Caption label + back */}
              <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between' }}>
                <div style={{ fontSize:'13px', fontWeight:600, color:'var(--text-primary)' }}>Your caption</div>
                <button onClick={() => { setStep('settings'); setError('') }} style={{ fontSize:'12px', color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>← Edit settings</button>
              </div>

              {/* Editable caption */}
              <textarea value={caption} onChange={e => setCaption(e.target.value)} rows={13}
                style={{ width:'100%', border:'1px solid var(--border)', borderRadius:'12px', padding:'14px', fontSize:'13px', fontFamily:'inherit', color:'var(--text-primary)', background:'var(--bg-card)', resize:'vertical', outline:'none', lineHeight:1.7, boxSizing:'border-box' }}
                onFocus={e => e.target.style.borderColor = TEAL}
                onBlur={e => e.target.style.borderColor = 'var(--border)'} />

              <div style={{ fontSize:'11.5px', color:'var(--text-muted)' }}>
                Edit freely — this is exactly what will be posted. Tap "Post now" when ready.
              </div>

              {/* Image preview */}
              {product.image_url && (
                <div style={{ borderRadius:'10px', overflow:'hidden', border:'1px solid var(--border)' }}>
                  <img src={product.image_url} alt={product.name} style={{ width:'100%', maxHeight:'180px', objectFit:'cover', display:'block' }} />
                </div>
              )}

              {/* Regenerate */}
              <button onClick={handleGenerate} disabled={generating}
                style={{ width:'100%', padding:'10px', borderRadius:'10px', border:'1px solid var(--border)', background:'var(--bg-muted)', fontSize:'13px', fontWeight:600, color: generating ? 'var(--text-muted)' : 'var(--text-primary)', cursor: generating ? 'not-allowed' : 'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'6px' }}>
                {generating
                  ? <><div style={{ width:'14px', height:'14px', borderRadius:'50%', border:'2px solid rgba(0,0,0,0.15)', borderTopColor:'var(--text-muted)', animation:'fb-spin 0.7s linear infinite' }} />Regenerating...</>
                  : '↺ Regenerate caption'
                }
              </button>

              {error && (
                <div style={{ padding:'10px 14px', background:'#FEF2F2', border:'1px solid #FECACA', borderRadius:'10px', fontSize:'12.5px', color:'#DC2626' }}>{error}</div>
              )}

              {/* Post button */}
              <button onClick={handlePost} disabled={posting || !caption.trim()}
                style={{ width:'100%', padding:'13px', borderRadius:'12px', background: posting || !caption.trim() ? 'var(--border)' : FB_BLUE, border:'none', fontSize:'14px', fontWeight:700, color: posting || !caption.trim() ? 'var(--text-muted)' : '#fff', cursor: posting || !caption.trim() ? 'not-allowed' : 'pointer', fontFamily:'inherit', display:'flex', alignItems:'center', justifyContent:'center', gap:'8px' }}>
                {posting && <div style={{ width:'16px', height:'16px', borderRadius:'50%', border:'2px solid rgba(255,255,255,0.3)', borderTopColor:'#fff', animation:'fb-spin 0.7s linear infinite' }} />}
                {posting ? 'Posting to Facebook...' : scheduleMode && scheduleTime ? 'Schedule post →' : 'Post to Facebook now →'}
              </button>
            </div>
          )}

          {/* ══ STEP: Done ══════════════════════════════════════════════════ */}
          {step === 'done' && (
            <div style={{ padding:'40px 20px', textAlign:'center' }}>
              <div style={{ width:'64px', height:'64px', borderRadius:'50%', background:'#F0FDF4', border:'2px solid #BBF7D0', display:'flex', alignItems:'center', justifyContent:'center', margin:'0 auto 16px' }}>
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#16A34A" strokeWidth="2.5" strokeLinecap="round"><polyline points="20 6 9 17 4 12"/></svg>
              </div>
              <div style={{ fontSize:'20px', fontWeight:800, color:'var(--text-primary)', marginBottom:'8px' }}>
                {scheduleMode ? 'Post scheduled!' : 'Posted to Facebook!'}
              </div>
              <div style={{ fontSize:'13px', color:'var(--text-muted)', marginBottom:'24px', lineHeight:1.6 }}>
                {scheduleMode
                  ? `Your post is scheduled for ${scheduleTime ? new Date(scheduleTime).toLocaleString('en-BD') : 'the selected time'}.`
                  : 'Your product is now live on your Facebook page.'}
              </div>
              {postUrl && (
                <a href={postUrl} target="_blank" rel="noopener noreferrer"
                  style={{ display:'inline-flex', alignItems:'center', gap:'6px', padding:'10px 20px', borderRadius:'10px', background:FB_BLUE, color:'#fff', fontSize:'13px', fontWeight:700, textDecoration:'none', marginBottom:'12px' }}>
                  <svg width="14" height="14" viewBox="0 0 24 24" fill="#fff"><path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/></svg>
                  View post on Facebook
                </a>
              )}
              <div>
                <button onClick={onClose} style={{ fontSize:'13px', color:'var(--text-muted)', background:'none', border:'none', cursor:'pointer', fontFamily:'inherit' }}>Close</button>
              </div>
            </div>
          )}
        </div>
      </div>
      <style>{`@keyframes fb-spin { to { transform: rotate(360deg) } }`}</style>
    </div>
  )
}